// @flow

import React from 'react';
import { get } from 'lodash';
import { withRouter } from 'next/router';

import { Screen } from '@zapier/theme';
import { Spacer } from '@zapier/design-system';
import { useMediaProps } from '@zapier/common-components';

import AppAlternativesOverview from 'web-ssr/app/app-directory/components/AppAlternativesOverview';
import AppDirectoryLayout from 'web-ssr/app/app-directory/components/AppDirectoryLayout';
import AppIntegrationBrowser from 'web-ssr/app/app-directory/components/AppIntegrationBrowser';
import AppIntegrationDetails from 'web-ssr/app/app-directory/components/AppIntegrationDetails';
import AppIntegrationPromo from 'web-ssr/app/app-directory/components/AppIntegrationPromo';
import AppOverview from 'web-ssr/app/app-directory/components/AppOverview';
import AppZapTemplates from 'web-ssr/app/app-directory/components/AppZapTemplates';
import EarlyAccessButton from 'web-ssr/app/app-directory/components/EarlyAccessButton';
import UpcomingAppRequestForm from 'web-ssr/app/app-directory/components/UpcomingAppRequestForm';
import appDirectoryFeaturesQuery from 'web-ssr/app/app-directory/queries/appDirectoryFeaturesQuery';
import { buildServiceSharingDescription } from 'web-ssr/app/common/components/SharingMetaTags';
import { createAppIntegrationsPageLink } from 'web-ssr/app/app-directory/utils';
import { getSelectedAppSlug } from 'web-ssr/app/common/next/utils';
import { isAppPageAvailable } from 'web-ssr/app/model/AppV2';
import {
  createAbsoluteURL,
  createServiceIntegrationsPageLink,
} from 'web-ssr/app/common/utils/AppUtils';

import type { RouterProps } from 'web-ssr/app/common/next/types';
import type { appDirectoryFeaturesQuery_appBySlug } from 'web-ssr/graphql/types';

type Props = {
  app: appDirectoryFeaturesQuery_appBySlug,
  router: RouterProps,
};

const AppDirectoryOneAppIntegrationsPage = (props: Props) => {
  const { app } = props;

  const { size } = useMediaProps({
    [Screen.queries.phone]: { size: 'small' },
    [Screen.queries.phablet]: { size: 'small' },
    [Screen.queries.tablet]: { size: 'medium' },
    [Screen.queries.desktop]: { size: 'large' },
  });

  const documentTitle = `${app.name} Integrations | Connect Your Apps with Zapier`;
  const documentDescription = buildServiceSharingDescription(
    app.name,
    app.isUpcoming
  );

  return (
    <AppDirectoryLayout
      canonicalUrl={createAbsoluteURL(
        createServiceIntegrationsPageLink(app.slug)
      )}
      documentDescription={documentDescription}
      documentTitle={documentTitle}
      extraCrumbs={[
        {
          text: 'Integrations',
          href: createAppIntegrationsPageLink(app.slug),
        },
      ]}
      pageSubTitle={`${app.name} Integrations`}
      pageTitle={app.name}
      primaryApp={app}
      renderOverview={() => <AppOverview primaryAppSlug={app.slug} />}
    >
      {!app.isUpcoming && !app.inviteUrl && (
        <div>
          <AppIntegrationPromo primaryAppSlug={app.slug} />
          <Spacer height={size === 'medium' ? '40px' : '30px'} />
          <AppIntegrationBrowser primaryAppSlug={app.slug} />
        </div>
      )}

      {!app.isUpcoming && !app.inviteUrl && (
        <AppZapTemplates primaryAppSlug={app.slug} />
      )}
      {!app.isUpcoming && !app.inviteUrl && (
        <AppIntegrationDetails primaryAppSlug={app.slug} />
      )}

      {app.isUpcoming && (
        <div>
          <UpcomingAppRequestForm appSlug={app.slug} />
          <AppAlternativesOverview appSlug={app.slug} />
        </div>
      )}
      {app.inviteUrl && <EarlyAccessButton appSlug={app.slug} />}
    </AppDirectoryLayout>
  );
};

AppDirectoryOneAppIntegrationsPage.getInitialProps = async context => {
  const appSlug = getSelectedAppSlug(context, null);

  const queryResult = await context.apolloClient.query({
    query: appDirectoryFeaturesQuery,
    variables: {
      slug: appSlug,
    },
    errorPolicy: 'all',
  });

  const app = get(queryResult, ['data', 'appBySlug']);

  // Redirect to a 404 page if this page does not exist, redirect them to a 404
  if (!isAppPageAvailable(app)) {
    context.res.statusCode = 404;
    return {};
  }

  return {
    app,
  };
};

export default withRouter(AppDirectoryOneAppIntegrationsPage);
